import { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from "@/components/ui/tooltip";
import { ArrowLeft, Save, Eye, Keyboard, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { BlocksPalette } from "@/components/proposals/BlocksPalette";
import { FreeFormCanvas } from "@/components/proposals/FreeFormCanvas";
import { StyleEditor } from "@/components/proposals/StyleEditor";
import { ProposalAIAssistant } from "@/components/proposals/ProposalAIAssistant";
import { ProposalPreviewDialog } from "@/components/proposals/ProposalPreviewDialog";
import { WelcomeDialog } from "@/components/proposals/WelcomeDialog";
import { KeyboardShortcutsDialog } from "@/components/proposals/KeyboardShortcutsDialog";
import { QuickStartTemplates } from "@/components/proposals/QuickStartTemplates";
import { PackageLineItemsDialog } from "@/components/proposals/PackageLineItemsDialog";
import { CoverPhotoSearch } from "@/components/proposals/CoverPhotoSearch";
import type { TemplateLayout, DocType, Page, Block } from "@/types/proposals";
import { nanoid } from "nanoid";
import { useAuth } from "@/hooks/useAuth";
import { getAllCoverPhotos } from "@/utils/coverPhotos";

export default function TemplateBuilder() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [templateName, setTemplateName] = useState("Untitled Template");
  const [layout, setLayout] = useState<TemplateLayout>({
    pages: [
      {
        id: nanoid(),
        title: "Page 1",
        order: 1,
        blocks: [],
      },
    ],
    variables: [],
    styles: {},
  });
  const [selectedBlock, setSelectedBlock] = useState<Block | null>(null);
  const [selectedBlockIds, setSelectedBlockIds] = useState<string[]>([]);
  const [currentPageId, setCurrentPageId] = useState<string>(layout.pages[0]?.id);
  const [loading, setLoading] = useState(false);
  const [orgId, setOrgId] = useState<string>("");
  const [docType, setDocType] = useState<DocType>("proposal");
  const [showPreview, setShowPreview] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);
  const [showShortcuts, setShowShortcuts] = useState(false);
  const [showPackageDialog, setShowPackageDialog] = useState(false);
  const [packageDialogBlock, setPackageDialogBlock] = useState<Block | null>(null);
  const [leftTab, setLeftTab] = useState<"blocks" | "templates">("blocks");
  const [rightTab, setRightTab] = useState<"style" | "photos" | "ai">("style");
  const { user, orgId: authOrgId, loading: authLoading } = useAuth();

  useEffect(() => {
    if (!authLoading && user) {
      loadTemplate();
    }
  }, [id, authLoading, user]);

  useEffect(() => {
    if (authOrgId) {
      setOrgId(authOrgId);
    }
    
    // Check if welcome dialog should be shown
    const hideWelcome = localStorage.getItem("hideTemplateBuilderWelcome");
    if (!hideWelcome) {
      setShowWelcome(true);
    }

    // Listen for restore layout events
    const handleRestore = (e: any) => {
      setLayout((prev) => ({ ...prev, pages: e.detail.pages }));
      if (e.detail.pages && e.detail.pages.length > 0) {
        setCurrentPageId(e.detail.pages[0].id);
      }
    };

    // Listen for package dialog events
    const handleOpenPackageDialog = (e: any) => {
      setPackageDialogBlock(e.detail.block);
      setShowPackageDialog(true);
    };

    window.addEventListener('restoreLayout', handleRestore);
    window.addEventListener('openPackageItemsDialog', handleOpenPackageDialog);
    
    return () => {
      window.removeEventListener('restoreLayout', handleRestore);
      window.removeEventListener('openPackageItemsDialog', handleOpenPackageDialog);
    };
  }, [id]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyboard = (e: KeyboardEvent) => {
      const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
      const cmdOrCtrl = isMac ? e.metaKey : e.ctrlKey;

      // Undo: Cmd/Ctrl + Z
      if (cmdOrCtrl && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        window.dispatchEvent(new CustomEvent('undo'));
      }

      // Redo: Cmd/Ctrl + Shift + Z
      if (cmdOrCtrl && e.key === 'z' && e.shiftKey) {
        e.preventDefault();
        window.dispatchEvent(new CustomEvent('redo'));
      }

      // Delete: Delete or Backspace
      if ((e.key === 'Delete' || e.key === 'Backspace') && selectedBlockIds.length > 0) {
        const active = document.activeElement as HTMLElement | null;
        
        // Don't delete block if user is typing in an input, textarea, or contentEditable
        if (
          active &&
          (active.tagName === 'INPUT' ||
           active.tagName === 'TEXTAREA' ||
           active.isContentEditable)
        ) {
          return;
        }

        e.preventDefault();
        selectedBlockIds.forEach(id => handleDeleteBlock(id));
      }

      // Duplicate: Cmd/Ctrl + D
      if (cmdOrCtrl && e.key === 'd' && selectedBlock) {
        e.preventDefault();
        const newBlock: Block = {
          ...selectedBlock,
          id: nanoid(),
          position: {
            ...selectedBlock.position,
            x: selectedBlock.position.x + 20,
            y: selectedBlock.position.y + 20,
          }
        };
        const updatedPages = layout.pages.map((page) =>
          page.id === currentPageId
            ? { ...page, blocks: [...page.blocks, newBlock] }
            : page
        );
        setLayout({ ...layout, pages: updatedPages });
        setSelectedBlock(newBlock);
        setSelectedBlockIds([newBlock.id]);
      }

      // Toggle Grid: Cmd/Ctrl + G
      if (cmdOrCtrl && e.key === 'g') {
        e.preventDefault();
        window.dispatchEvent(new CustomEvent('toggleGrid'));
      }

      // Zoom In: Cmd/Ctrl + =
      if (cmdOrCtrl && e.key === '=') {
        e.preventDefault();
        window.dispatchEvent(new CustomEvent('zoomIn'));
      }

      // Zoom Out: Cmd/Ctrl + -
      if (cmdOrCtrl && e.key === '-') {
        e.preventDefault();
        window.dispatchEvent(new CustomEvent('zoomOut'));
      }

      // Reset Zoom: Cmd/Ctrl + 0
      if (cmdOrCtrl && e.key === '0') {
        e.preventDefault();
        window.dispatchEvent(new CustomEvent('resetZoom'));
      }

      // Deselect: Escape
      if (e.key === 'Escape') {
        setSelectedBlockIds([]);
        setSelectedBlock(null);
      }

      // Show Shortcuts: ?
      if (e.key === '?' && !e.shiftKey) {
        e.preventDefault();
        setShowShortcuts(true);
      }
    };

    window.addEventListener('keydown', handleKeyboard);
    return () => window.removeEventListener('keydown', handleKeyboard);
  }, [selectedBlock, selectedBlockIds, currentPageId, layout]);

  const loadTemplate = async () => {
    try {
      if (!user || !authOrgId) return;

      if (id && id !== "new") {
        // First try to load as a proposal document (for viewing created proposals)
        const { data: proposalDoc, error: proposalError } = await supabase
          .from("proposal_documents")
          .select("*")
          .eq("id", id)
          .maybeSingle();

        if (proposalDoc) {
          // Loading a proposal document for viewing/editing
          setTemplateName(proposalDoc.title || "Untitled Proposal");
          const layoutData = proposalDoc.layout_filled_json as unknown as TemplateLayout;
          
          // Sanitize layout data
          const sanitizedLayout = {
            ...layoutData,
            pages: (layoutData.pages || []).map(page => ({
              ...page,
              blocks: (page.blocks || []).map(block => ({
                ...block,
                position: block.position || { x: 48, y: 100, width: 300, height: 100, zIndex: 1 },
              })),
            })),
          };
          
          setLayout(sanitizedLayout);
          setDocType(proposalDoc.doc_type as DocType);
          if (sanitizedLayout.pages && sanitizedLayout.pages.length > 0) {
            setCurrentPageId(sanitizedLayout.pages[0].id);
          }
          return;
        }

        // If not a proposal, try loading as a template
        const { data: templateData, error: templateError } = await supabase
          .from("proposal_templates")
          .select("*")
          .eq("id", id)
          .maybeSingle();

        if (templateError) {
          console.error("Template error:", templateError);
          throw new Error("Failed to load template");
        }

        if (templateData) {
          setTemplateName(templateData.name);
          const layoutData = templateData.layout_json as unknown as TemplateLayout;
          
          // Sanitize layout data
          const sanitizedLayout = {
            ...layoutData,
            pages: (layoutData.pages || []).map(page => ({
              ...page,
              blocks: (page.blocks || []).map(block => ({
                ...block,
                position: block.position || { x: 48, y: 100, width: 300, height: 100, zIndex: 1 },
              })),
            })),
          };
          
          setLayout(sanitizedLayout);
          setDocType(templateData.doc_type as DocType);
          if (sanitizedLayout.pages && sanitizedLayout.pages.length > 0) {
            setCurrentPageId(sanitizedLayout.pages[0].id);
          }
        } else {
          toast.error("Template not found");
          navigate("/templates");
        }
      } else {
        const type = searchParams.get("type") as DocType || "proposal";
        setDocType(type);
      }
    } catch (error) {
      console.error("Error loading template:", error);
      toast.error("Failed to load template");
      navigate("/templates");
    }
  };

  const handleSave = async () => {
    if (!orgId || !user) return;
    
    setLoading(true);
    try {

      const templateData = {
        org_id: orgId,
        name: templateName,
        doc_type: docType,
        layout_json: layout as any,
        updated_by: user.id,
      };

      if (id && id !== "new") {
        const { error } = await supabase
          .from("proposal_templates")
          .update(templateData)
          .eq("id", id);

        if (error) throw error;
        toast.success("Template saved successfully");
      } else {
        const { data, error } = await supabase
          .from("proposal_templates")
          .insert([{
            ...templateData,
            created_by: user.id,
          }])
          .select()
          .single();

        if (error) throw error;
        toast.success("Template created successfully");
        navigate(`/templates/builder/${data.id}`, { replace: true });
      }
    } catch (error) {
      console.error("Error saving template:", error);
      toast.error("Failed to save template");
    } finally {
      setLoading(false);
    }
  };

  const handleAddPage = () => {
    const newPage: Page = {
      id: nanoid(),
      title: `Page ${layout.pages.length + 1}`,
      order: layout.pages.length + 1,
      blocks: [],
    };
    setLayout({
      ...layout,
      pages: [...layout.pages, newPage],
    });
    setCurrentPageId(newPage.id);
  };

  const handleAddBlock = (blockType: string, position?: { x: number; y: number }) => {
    const currentPage = layout.pages.find((p) => p.id === currentPageId);
    if (!currentPage) return;

    const defaultPosition = getDefaultBlockPosition(blockType, currentPage.blocks, position);

    const newBlock: Block = {
      id: nanoid(),
      type: blockType as any,
      order: currentPage.blocks.length + 1,
      position: defaultPosition,
      style: {},
      content: getDefaultBlockContent(blockType),
    };

    const updatedPages = layout.pages.map((page) =>
      page.id === currentPageId
        ? { ...page, blocks: [...page.blocks, newBlock] }
        : page
    );

    setLayout({ ...layout, pages: updatedPages });
    setSelectedBlock(newBlock);
    setSelectedBlockIds([newBlock.id]);
    setRightTab("style");
  };

  const getDefaultBlockPosition = (blockType: string, existingBlocks: Block[], position?: { x: number; y: number }) => {
    if (position) {
      return { ...position, width: 300, height: 100, zIndex: existingBlocks.length };
    }

    const defaults: Record<string, any> = {
      cover: { x: 0, y: 0, width: 816, height: 1056, zIndex: 0 }, // Full page cover
      text: { x: 48, y: 100, width: 720, height: 'auto', zIndex: 1 },
      image: { x: 48, y: 100, width: 300, height: 200, zIndex: 1 },
      dynamic_image: { x: 48, y: 100, width: 200, height: 200, zIndex: 1 },
      smart_field: { x: 48, y: 100, width: 300, height: 40, zIndex: 1 },
      line_items_table: { x: 48, y: 100, width: 720, height: 'auto', zIndex: 1 },
      signature: { x: 48, y: 100, width: 400, height: 150, zIndex: 1 },
      terms: { x: 48, y: 100, width: 720, height: 'auto', zIndex: 1 },
      shape: { x: 200, y: 200, width: 200, height: 200, zIndex: 1 },
      icon: { x: 200, y: 200, width: 100, height: 100, zIndex: 1 },
    };

    const defaultPos = defaults[blockType] || { x: 48, y: 100, width: 300, height: 100, zIndex: 1 };

    const overlaps = existingBlocks.some(block => {
      if (!block.position) return false;
      return Math.abs(block.position.x - defaultPos.x) < 20 &&
             Math.abs(block.position.y - defaultPos.y) < 20;
    });

    if (overlaps) {
      return {
        ...defaultPos,
        x: defaultPos.x + 20,
        y: defaultPos.y + 20,
      };
    }

    return defaultPos;
  };

  const getDefaultBlockContent = (blockType: string) => {
    switch (blockType) {
      case "cover":
        return {
          headline: "{{org_name}}",
          subheadline: "Professional Proposal",
          customerLine: "Prepared for {{customer_name}}",
          addressLine: "{{property_address}}",
          dateLine: "{{current_date}}",
          backgroundImage: "/cover-photos/roof1.jpg",
        };
      case "text":
        return { text: "Enter your content here..." };
      case "line_items_table":
        return { items: [], showUnitPrice: true, showQuantity: true, showTotal: true };
      case "good_better_best":
        return { options: [] };
      case "totals_summary":
        return { subtotal: 0, tax: 0, discount: 0, total: 0 };
      case "signature":
        return { label: "Customer Signature" };
      case "terms":
        return { terms: "" };
      case "marketing_gallery":
        return { images: [] };
      case "shape":
        return { shapeType: 'rectangle', fillColor: '#3B82F6', strokeColor: '#1E40AF', strokeWidth: 2, rotation: 0 };
      case "icon":
        return { iconName: 'Star', strokeWidth: 2 };
      default:
        return {};
    }
  };

  const handleUpdateBlock = (blockId: string, updates: Partial<Block>) => {
    const updatedPages = layout.pages.map((page) => ({
      ...page,
      blocks: page.blocks.map((block) =>
        block.id === blockId ? { ...block, ...updates } : block
      ),
    }));
    setLayout({ ...layout, pages: updatedPages });
    
    if (selectedBlock?.id === blockId) {
      setSelectedBlock({ ...selectedBlock, ...updates });
    }
  };

  const handleDeleteBlock = (blockId: string) => {
    const updatedPages = layout.pages.map((page) => ({
      ...page,
      blocks: page.blocks.filter((block) => block.id !== blockId),
    }));
    setLayout({ ...layout, pages: updatedPages });
    if (selectedBlock?.id === blockId) {
      setSelectedBlock(null);
    }
  };

  const handleReorderBlocks = (pageId: string, reorderedBlocks: Block[]) => {
    const updatedPages = layout.pages.map((page) =>
      page.id === pageId
        ? { ...page, blocks: reorderedBlocks }
        : page
    );

    setLayout({ ...layout, pages: updatedPages });
  };

  const handleSelectTemplate = (templateLayout: TemplateLayout, name: string) => {
    setLayout(templateLayout);
    setTemplateName(name);
    if (templateLayout.pages && templateLayout.pages.length > 0) {
      setCurrentPageId(templateLayout.pages[0].id);
    }
    setLeftTab("blocks");
    toast.success(`${name} template loaded`);
  };

  const currentPage = layout.pages.find((p) => p.id === currentPageId);

  // Listen for drag-and-drop from palette
  useEffect(() => {
    const handleAddBlockAtPosition = (e: any) => {
      handleAddBlock(e.detail.blockType, { x: e.detail.x, y: e.detail.y });
    };

    window.addEventListener('addBlockAtPosition', handleAddBlockAtPosition);
    return () => window.removeEventListener('addBlockAtPosition', handleAddBlockAtPosition);
  }, [currentPageId, layout]);

  return (
    <TooltipProvider>
      <div className="h-screen flex flex-col bg-background">
        {/* Top Toolbar */}
        <div className="border-b bg-card px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="sm" onClick={() => navigate("/templates")}>
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
              </TooltipTrigger>
              <TooltipContent>Back to Templates</TooltipContent>
            </Tooltip>
            <Input
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
              className="w-64"
              placeholder="Template name..."
            />
          </div>
          <div className="flex items-center gap-2">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="outline" size="sm" onClick={() => setShowShortcuts(true)}>
                  <Keyboard className="w-4 h-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Keyboard Shortcuts</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="outline" size="sm" onClick={() => setShowPreview(true)}>
                  <Eye className="w-4 h-4 mr-2" />
                  Preview
                </Button>
              </TooltipTrigger>
              <TooltipContent>Preview Template</TooltipContent>
            </Tooltip>
            <Button onClick={handleSave} disabled={loading} size="sm">
              <Save className="w-4 h-4 mr-2" />
              {loading ? "Saving..." : "Save"}
            </Button>
          </div>
        </div>

        {/* Main Builder Area */}
        <div className="flex-1 flex overflow-hidden">
          {/* Left Sidebar - Blocks Palette / Templates */}
          <div className="w-64 border-r bg-card flex flex-col">
            <Tabs value={leftTab} onValueChange={(v: any) => setLeftTab(v)} className="flex-1 flex flex-col">
              <TabsList className="grid grid-cols-2 mx-2 mt-2">
                <TabsTrigger value="blocks" className="text-xs">Blocks</TabsTrigger>
                <TabsTrigger value="templates" className="text-xs">Templates</TabsTrigger>
              </TabsList>

              <TabsContent value="blocks" className="flex-1 mt-0 overflow-hidden">
                <BlocksPalette onAddBlock={handleAddBlock} />
              </TabsContent>

              <TabsContent value="templates" className="flex-1 mt-0 overflow-auto">
                <QuickStartTemplates onSelectTemplate={handleSelectTemplate} />
              </TabsContent>
            </Tabs>
          </div>

          {/* Canvas */}
          <FreeFormCanvas
            currentPage={currentPage}
            onSelectBlock={(block) => {
              setSelectedBlock(block);
              setSelectedBlockIds([block.id]);
              setRightTab(block.type === "cover" ? "photos" : "style");
            }}
            onUpdateBlock={handleUpdateBlock}
            onDeleteBlock={handleDeleteBlock}
            onReorderBlocks={handleReorderBlocks}
            pages={layout.pages}
            currentPageId={currentPageId}
            onPageChange={setCurrentPageId}
            onAddPage={handleAddPage}
            selectedBlockIds={selectedBlockIds}
            onMultiSelect={setSelectedBlockIds}
          />

          {/* Right Sidebar - Style Editor & AI */}
          <div className="w-80 border-l bg-card flex flex-col">
            <Tabs value={rightTab} onValueChange={(v: any) => setRightTab(v)} className="flex-1 flex flex-col">
              <TabsList className="grid grid-cols-3 mx-2 mt-2">
                <TabsTrigger value="style" className="text-xs">Style</TabsTrigger>
                <TabsTrigger value="photos" className="text-xs">Photos</TabsTrigger>
                <TabsTrigger value="ai" className="text-xs">
                  <Sparkles className="w-3 h-3 mr-1" />
                  AI
                </TabsTrigger>
              </TabsList>

              <TabsContent value="style" className="flex-1 p-4 overflow-y-auto mt-0">
                <StyleEditor
                  block={selectedBlock}
                  onUpdateBlock={handleUpdateBlock}
                />
              </TabsContent>

              <TabsContent value="photos" className="flex-1 p-4 overflow-y-auto mt-0">
                {selectedBlock?.type === 'cover' ? (
                  <CoverPhotoSearch 
                    onSelectPhoto={(photoUrl) => {
                      if (selectedBlock) {
                        handleUpdateBlock(selectedBlock.id, {
                          content: { ...selectedBlock.content, propertyPhoto: photoUrl }
                        });
                      }
                    }}
                  />
                ) : (
                  <div className="text-sm text-muted-foreground text-center py-8">
                    Select a cover block to choose photos
                  </div>
                )}
              </TabsContent>

              <TabsContent value="ai" className="flex-1 p-4 overflow-y-auto mt-0">
                <ProposalAIAssistant
                  onContentGenerated={(content, action) => {
                    const currentPage = layout.pages.find((p) => p.id === currentPageId);
                    if (!currentPage) return;

                    let blockContent: any;
                    let requiredBlockType: string;

                    switch (action) {
                      case "generate_cover":
                        requiredBlockType = "cover";
                        blockContent = content;
                        break;
                      case "generate_scope":
                      case "generate_marketing":
                      case "custom":
                        requiredBlockType = "text";
                        blockContent = { text: typeof content === "string" ? content : JSON.stringify(content) };
                        break;
                      case "generate_terms":
                        requiredBlockType = "terms";
                        blockContent = { terms: content };
                        break;
                      default:
                        return;
                    }

                    if (selectedBlock && selectedBlock.type === requiredBlockType) {
                      handleUpdateBlock(selectedBlock.id, { content: blockContent });
                    } else {
                      const defaultPos = getDefaultBlockPosition(requiredBlockType, currentPage.blocks);
                      const newBlock: Block = {
                        id: nanoid(),
                        type: requiredBlockType as any,
                        order: currentPage.blocks.length + 1,
                        position: defaultPos,
                        style: {},
                        content: blockContent,
                      };

                      const updatedPages = layout.pages.map((page) =>
                        page.id === currentPageId
                          ? { ...page, blocks: [...page.blocks, newBlock] }
                          : page
                      );

                      setLayout({ ...layout, pages: updatedPages });
                      setSelectedBlock(newBlock);
                    }
                  }}
                  context={{ orgId, templateName, docType }}
                />
              </TabsContent>
            </Tabs>
          </div>
        </div>

        {/* Dialogs */}
        <ProposalPreviewDialog
          open={showPreview}
          onOpenChange={setShowPreview}
          layout={layout}
          title={templateName}
        />

        <WelcomeDialog
          open={showWelcome}
          onOpenChange={setShowWelcome}
        />

        <KeyboardShortcutsDialog
          open={showShortcuts}
          onOpenChange={setShowShortcuts}
        />

        {showPackageDialog && packageDialogBlock && (
          <PackageLineItemsDialog
            open={showPackageDialog}
            onOpenChange={setShowPackageDialog}
            block={packageDialogBlock}
            onUpdate={(updates) => handleUpdateBlock(packageDialogBlock.id, updates)}
          />
        )}
      </div>
    </TooltipProvider>
  );
}
